import React from 'react';
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    XCircleIcon,
} from '@heroicons/react/24/solid';

interface AlertProps {
    variant: 'success' | 'warning' | 'error' | 'info';
    title?: string;
    children: React.ReactNode;
    onClose?: () => void;
    className?: string;
}

export const Alert: React.FC<AlertProps> = ({
    variant,
    title,
    children,
    onClose,
    className = '',
}) => {
    const variants = {
        success: {
            container: 'bg-success-50 border-success-200 text-success-800',
            icon: <CheckCircleIcon className="h-5 w-5 text-success-600" />,
        },
        warning: {
            container: 'bg-amber-50 border-amber-200 text-amber-800',
            icon: <ExclamationTriangleIcon className="h-5 w-5 text-warning-600" />,
        },
        error: {
            container: 'bg-red-50 border-red-200 text-red-800',
            icon: <XCircleIcon className="h-5 w-5 text-danger-600" />,
        },
        info: {
            container: 'bg-blue-50 border-blue-200 text-blue-800',
            icon: <InformationCircleIcon className="h-5 w-5 text-primary-600" />,
        },
    };

    const config = variants[variant];

    return (
        <div
            className={`border rounded-md p-4 ${config.container} ${className}`}
            role="alert"
        >
            <div className="flex">
                <div className="flex-shrink-0">{config.icon}</div>
                <div className="ml-3 flex-1">
                    {title && <h3 className="text-sm font-medium mb-1">{title}</h3>}
                    <div className="text-sm">{children}</div>
                </div>
                {onClose && (
                    <div className="ml-auto pl-3">
                        <button
                            onClick={onClose}
                            className="inline-flex rounded-md p-1.5 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-2"
                            aria-label="Dismiss alert"
                        >
                            <XCircleIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

